namespace Ir.Components.ReportDesigner.ComparisonFilterList {

    interface IViewModel {
        comparisonFilters: KnockoutObservableArray<ComparisonFilter>;
        sortableFieldsConfig: DevExpress.ui.dxSortable.Properties;
        addButtonConfig: DevExpress.ui.dxButton.Properties;
        clearButtonConfig: DevExpress.ui.dxButton.Properties;
        onRemove: (comparisonFilter: ComparisonFilter) => void;

        reportConfig: ReportConfig;
        fields: KnockoutObservableArray<MetricField | ReportField>;
        metrics: KnockoutObservableArray<MetricField>;
        metricArray: KnockoutObservable<KnockoutObservableArray<MetricField>>;
        widgetType: KnockoutObservable<WidgetType>;
        typeLabelVisible: boolean;
        isReportTemplate: boolean;
    }

    export interface IParams {
        comparisonFilters: KnockoutObservableArray<ComparisonFilter>;
        reportConfig: KnockoutObservable<ReportConfig>;
        metricArray: KnockoutObservable<KnockoutObservableArray<MetricField>>;
        onChanged?: Function;
        widgetType: KnockoutObservable<WidgetType>;
        reportDesigner: Ir.Components.ReportDesigner.ReportDesigner.IViewModel;
    }

    export function getViewModel(this: IViewModel, params: IParams, componentInfo) {
        let self = this;
        if ((params as any).config) {
            params = (params as any).config;
        }
        self.comparisonFilters = params.comparisonFilters;
        self.reportConfig = params.reportConfig();
        self.metricArray = params.metricArray;
        self.widgetType = params.widgetType;
        self.typeLabelVisible = params.reportDesigner.openAsPopup;
        self.isReportTemplate = params.reportDesigner.openingByAdmin !== null;

        self.sortableFieldsConfig = {
            data: { fields: self.comparisonFilters },
            filter: 'report-designer-comparison-filter-item',
            dragDirection: "vertical",
            itemOrientation: 'vertical',
            moveItemOnDrop: false,
            onReorder(e) {
                if (e.toIndex >= 0) {
                    let fromField = self.comparisonFilters.splice(e.fromIndex, 1)[0];
                    self.comparisonFilters.splice(e.toIndex, 0, fromField);
                }
            },
        };
        self.addButtonConfig = {
            hint: "Add a new comparison filter",
            icon: "plus",
            stylingMode: 'contained',
            type: "success",
            height: 22,
            width: 24,
            elementAttr: {
                class: "removePadding"
            },
            onClick(e) {
                Ir.Components.ReportDesigner.ReportDesigner.hidePopovers();
                addComparisonFilter(self);
                params.onChanged?.();
            }
        };

        self.clearButtonConfig = {
            hint: "Clear comparison filters",
            icon: "clearformat",
            stylingMode: 'contained',
            type: "danger",
            height: 22,
            width: 24,
            elementAttr: {
                class: "removePadding"
            },
            onClick(e) {
                Ir.Components.ReportDesigner.ReportDesigner.hidePopovers();
                removeAllComparisonFilters(self);
                params.onChanged?.();
            }
        };

        self.onRemove = function (comparisonFilter: ComparisonFilter) {
            removeComparisonFilter(self, comparisonFilter);
            params.onChanged?.();
        }
    }

    function removeComparisonFilter(self: IViewModel, comparisonFilter: ComparisonFilter) {
        self.comparisonFilters.remove(comparisonFilter);
        removeMetricFields(self, comparisonFilter);
    }

    function removeMetricFields(self: IViewModel, comparisonFilter: ComparisonFilter) {
        self.metricArray().remove(function (metricField) { return metricField.isMetric && metricField.metricContainer.comparisonFilterId === comparisonFilter.id(); });
    }

    function removeAllComparisonFilters(self: IViewModel) {
        for (let comparisonFilter of self.comparisonFilters()) {
            removeMetricFields(self, comparisonFilter);
        }
        self.comparisonFilters.removeAll();
    }

    function addComparisonFilter(self: IViewModel) {
        let metricArray = self.metricArray()();
        if (metricArray.filter(x => x.id() === allMetricName || x.displayName() === allMetricName).length > 0) {
            DevExpress.ui.notify("It is not allowed to add the comparison filter when there is a ALL metric", "warning");
            return;
        }

        if (metricArray.filter(x => x.isReportMetric && (x.metricContainer.comparisonFilterId === null || x.metricContainer.comparisonFilterId === undefined)).length > 0) {
            DevExpress.ui.notify("It is not allowed to add the comparison filter when there is a field converted to metric", "warning");
            return;
        }
        let comparisonColors = ["#264653", "#2a9d8f", "#e9c46a", "#f4a261", "#e76f51", "#e63946", "#a5a58d", "#6a994e", "#4f5d75"];
        var color = comparisonColors[self.comparisonFilters().length];
        var id = 1;
        while (self.comparisonFilters().find(x => x.id() === id.toString())) {
            id++;
        }

        var filter = new ComparisonFilter({
            id: id.toString(),
            name: "Filter " + id,
            color: color,
            showValue: true,
            showVariation: true,
            showPercentageVariation: true,
            dateOption: ComparisonDateOptionEnum.Previous,
            dateUnit: ComparisonDateUnitEnum.Month,
            dateUnitValue: 1,
            dateRange: 17,
            dateField: null,
            filters: [],
            comparisonFilterIdBase: ""
        });
        
        self.comparisonFilters.push(filter);
        addComparisonMetricsForFilterInternal(self, filter)
    }

    function addComparisonMetricsForFilterInternal(self: IViewModel, filter: ComparisonFilter) {
        addComparisonMetricsForFilter(filter, self.widgetType(), self.metricArray());
    }
    export function addComparisonMetricsForFilter(filter: ComparisonFilter, widgetType: WidgetType, metricArray: KnockoutObservableArray<MetricField>) {
        resolveComparison(widgetType, metricArray, true, ComparisonMetricType.Value, filter);
        resolveComparison(widgetType, metricArray, true, ComparisonMetricType.Variation, filter);
        resolveComparison(widgetType, metricArray, true, ComparisonMetricType.PercentageVariation, filter);
    }

    function resolveComparison(widgetType: WidgetType, metricArray: KnockoutObservableArray<MetricField>, newValue: boolean, comparisonType: ComparisonMetricType, filter: ComparisonFilter) {
        if (newValue) {
            metricArray().filter(x => x.isMetric && x.metricContainer.comparisonType === ComparisonMetricType.None).forEach(function (field) {
                resolveComparisonForMetric(widgetType, metricArray, field, filter, comparisonType);
            });
        }
        else {
            metricArray.remove(function (reportField) {
                return reportField.isMetric && reportField.metricContainer.comparisonType === comparisonType && reportField.metricContainer.comparisonFilterId === filter.id();
            });
        }
    }

    function resolveComparisonForMetric(widgetType: WidgetType, metricArray: KnockoutObservableArray<MetricField>, metricField: MetricField, filter: ComparisonFilter, comparisonType: ComparisonMetricType) {
        if (canHaveComparison(metricField, widgetType)) {
            let data = ko.toJS(metricField);
            let newMetric = new MetricField(data, metricField.isOriginalTemplate);

            newMetric.metricContainer.comparisonType = comparisonType;
            if (comparisonType === ComparisonMetricType.PercentageVariation)
                newMetric.numberFormat(FieldFormatEnum.Percentage);
            newMetric.metricContainer.comparisonFilterId = filter.id();
            newMetric.id(""); //remove the id, need a new one when saving
            let comparisonName = comparisonType === ComparisonMetricType.Value ? "" : (comparisonType === ComparisonMetricType.Variation ? " Var." : " % Var.");
            let name = `${(newMetric.customDisplayName() ?? newMetric.displayName())} ${filter.name()}${comparisonName}`;
            newMetric.displayName(null);
            newMetric.customDisplayName(name);
            newMetric.isReportMetric = true;
            newMetric.parentMetricField = metricField;

            if (!metricArray().find(x => x.queryName === newMetric.queryName)) {
                metricArray.push(newMetric);
            }
        }
    }

    export function addComparisonMetrics(metricField: MetricField, widgetType: WidgetType, metricArray: KnockoutObservableArray<MetricField>, comparisonFilters: KnockoutObservableArray<ComparisonFilter>) {
        if (canHaveComparison(metricField, widgetType)) {
            comparisonFilters().forEach(function (filter) {
                resolveComparisonForMetric(widgetType, metricArray, metricField, filter, ComparisonMetricType.Value);
                resolveComparisonForMetric(widgetType, metricArray, metricField, filter, ComparisonMetricType.Variation);
                resolveComparisonForMetric(widgetType, metricArray, metricField, filter, ComparisonMetricType.PercentageVariation);
            });
        }
    }

    export function canHaveComparison(metricField: MetricField, widgetType: WidgetType) {
        if (metricField.id() === allMetricName ||
            //Expression metrics created in report designer with any selected metric
            metricField.metricContainer.isExpression ||
            //Only for Normal, PercentOfTotal and Accumulated
            (metricField.metricContainer.functionType() > FunctionTypeEnum.AccumulatedAll && metricField.metricContainer.functionType() !== FunctionTypeEnum.Rank) ||
            //Accumulated
            (metricField.metricContainer.functionType() == FunctionTypeEnum.Accumulated && widgetType !== WidgetType.Chart)
            
        ) {
            return false;
        }
        return true;
    }
}